import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useRequestFilters } from '@/modules/service-desk/shared/use-request-filters';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-desk/shared/components/applications-table';
import { buildNocFilter, NocTravelRange } from '../../utils/noc-filter';

/** The toolbar's travel window is gone; travel dates are filtered from their column instead. */
const NO_TRAVEL: NocTravelRange = { from: '', to: '' };
import { useDeleteNoc, useNocApplications, useNocTypes, useReviewNoc } from '../../hooks/use-noc';
import { NocApplication, NocType } from '../../types/noc.types';
import { createNocRequestColumns } from '../noc-columns/noc-columns';
import { NocDetailsDialog } from '../noc-details-dialog/noc-details-dialog';
import { NocDocInfoDialog } from '../noc-doc-info-dialog/noc-doc-info-dialog';

export const NocRequests = () => {
  const { t } = useTranslation();
  const canReview = useHasPermission(HRM_PERMISSIONS.NOC_APPLICATION.REVIEW);
  const canGenerate = useHasPermission(HRM_PERMISSIONS.NOC_APPLICATION.GENERATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.NOC_APPLICATION.DELETE);

  // A head of department only sees the departments whose role they hold (plus their
  // own); only someone with DEPARTMENT.VIEW_ALL_DEPARTMENTS sees them all.
  const { filters, departments, departmentCondition, isScopeLoading } = useRequestFilters();
  const table = useApplicationsTable();
  const { resetPage } = table;
  const [detailsTarget, setDetailsTarget] = useState<NocApplication | null>(null);
  const [generateTarget, setGenerateTarget] = useState<NocApplication | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NocApplication | null>(null);
  const [decision, setDecision] = useState<{
    application: NocApplication;
    status: 'Accepted' | 'Rejected';
  } | null>(null);

  useEffect(() => resetPage(), [filters, resetPage]);

  const { data: nocTypes = [] as NocType[] } = useNocTypes();
  const { data, isLoading, error } = useNocApplications({
    pageNo: table.pagination.pageIndex + 1,
    pageSize: table.pagination.pageSize,
    filter: mergeWhere(buildNocFilter(filters, NO_TRAVEL, departmentCondition), table.columnWhere),
    order: table.order,
  });
  const { mutateAsync: review } = useReviewNoc();
  const { mutateAsync: deleteApplication } = useDeleteNoc();

  const columns = useMemo(
    () =>
      createNocRequestColumns({
        t,
        nocTypes,
        departments,
        canReview,
        canGenerate,
        canDelete,
        onApprove: (application) => setDecision({ application, status: 'Accepted' }),
        onReject: (application) => setDecision({ application, status: 'Rejected' }),
        onGenerate: setGenerateTarget,
        onDelete: setDeleteTarget,
      }),
    [t, nocTypes, departments, canReview, canGenerate, canDelete]
  );

  const approving = decision?.status === 'Accepted';

  return (
    <div className="flex flex-col gap-4">
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading || isScopeLoading}
        error={error}
        table={table}
        onRowClick={setDetailsTarget}
      />

      <NocDetailsDialog
        application={detailsTarget}
        nocTypes={nocTypes}
        onOpenChange={() => setDetailsTarget(null)}
      />
      <NocDocInfoDialog
        application={generateTarget}
        nocTypes={nocTypes}
        onOpenChange={() => setGenerateTarget(null)}
        onGenerated={(application) =>
          window.open(`/hr/service-desk/noc/report/${application.ItemId}`, '_blank')
        }
      />
      <ConfirmationModal
        open={Boolean(decision)}
        onOpenChange={(open) => !open && setDecision(null)}
        title={t(approving ? 'APPROVE_NOC' : 'REJECT_NOC')}
        description={t(approving ? 'ARE_YOU_SURE_TO_APPROVE_NOC' : 'ARE_YOU_SURE_TO_REJECT_NOC')}
        confirmText={t(approving ? 'APPROVE' : 'REJECT')}
        onConfirm={() =>
          decision && review({ itemId: decision.application.ItemId, status: decision.status })
        }
      />
      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('DELETE_NOC')}
        description={t('ARE_YOU_SURE_TO_DELETE_NOC')}
        confirmText={t('DELETE')}
        onConfirm={() => deleteTarget && deleteApplication(deleteTarget.ItemId)}
      />
    </div>
  );
};
